import { LitElement, css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/icon-button/icon-button.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/switch/switch.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import { WithUserProfileConsumer } from '../mixins/with-user-profile-consumer';
import { WithActiveShopConsumer } from '../mixins/with-active-shop-consumer';
import { ApiFactory } from '../api/easypos-api';
import { IStockLocation } from '../api/model/requests';
import { resolveRouterPath, router } from '../router';
import { styles as sharedStyles } from '../styles/shared-styles';

const SLUG_RX = /^[a-z0-9](?:[a-z0-9-]{0,46}[a-z0-9])?$/;

// Common cafe locations the empty-state "Seed common cafe locations"
// button inserts. Skippable — owners can name their own from scratch.
const SEED_LOCATIONS: Omit<IStockLocation, 'id'>[] = [
  { name: 'Display fridge', sortOrder: 10, isActive: true },
  { name: 'Syrup rail', sortOrder: 20, isActive: true },
  { name: 'Bean hopper', sortOrder: 30, isActive: true },
  { name: 'Walk-in', sortOrder: 40, isActive: true },
  { name: 'Dry store', sortOrder: 50, isActive: true },
  { name: 'Freezer', sortOrder: 60, isActive: true },
];

const Base = WithActiveShopConsumer(WithUserProfileConsumer(LitElement));

// Sub-phase K.2 — per-shop stock-take walk-order locations. Owner names
// each area of the shop (Display fridge, Walk-in, etc.), reorders them
// to match the physical walk, and tags each stock item with a location
// via the stock-edit page. The stocktake-edit page groups lines by
// location in SortOrder so the cashier walks the shop in physical
// order rather than alphabetically.
//
// Bulk-save model (mirrors menu-categories-page): operator edits the
// whole list, taps one Save that calls replaceStockLocations.
@customElement('stock-locations-page')
export class StockLocationsPage extends Base {
  @state() private _items: IStockLocation[] = [];
  @state() private _loading = true;
  @state() private _saving = false;
  @state() private _error: string | null = null;
  @state() private _saved = false;

  private _initStarted = false;

  static styles = [
    sharedStyles,
    css`
      :host {
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 0;
        padding: 16px;
        max-width: 760px;
        margin: 0 auto;
        box-sizing: border-box;
        width: 100%;
        gap: 12px;
      }
      header.bar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        flex-wrap: wrap;
        flex: none;
      }
      .actions {
        display: flex;
        gap: 8px;
        flex: none;
      }
      .empty {
        text-align: center;
        padding: 32px 12px;
        color: var(--sl-color-neutral-500);
        display: flex;
        flex-direction: column;
        gap: 12px;
        align-items: center;
      }
      .row-list {
        display: flex;
        flex-direction: column;
        gap: 8px;
        overflow-y: auto;
      }
      .row {
        display: grid;
        grid-template-columns: 32px 80px 1fr 90px 40px;
        gap: 8px;
        align-items: center;
        padding: 8px 10px;
        border: 1px solid var(--sl-color-neutral-200);
        border-radius: var(--sl-border-radius-medium);
        background: var(--app-surface, var(--sl-color-neutral-0));
      }
      .reorder {
        display: inline-flex;
        flex-direction: column;
        gap: 0;
      }
      .reorder sl-icon-button::part(base) {
        padding: 0;
        font-size: 0.95rem;
      }
      .small {
        font-size: 0.78rem;
        opacity: 0.7;
      }
      sl-alert {
        flex: none;
      }
    `,
  ];

  firstUpdated() {
    if (this.activeShop) void this._init();
  }

  updated(changed: Map<string, unknown>) {
    if (changed.has('activeShop') && this.activeShop && !this._initStarted) {
      void this._init();
    }
  }

  private async _init() {
    if (this._initStarted) return;
    this._initStarted = true;

    if (!this.activeShop) {
      this._error = 'No active shop. Pick one first.';
      this._loading = false;
      return;
    }
    if (!this.userProfile?.hasRole(this.activeShop.shopName, 'owner')) {
      this._error = 'Only the shop owner can manage stock locations.';
      this._loading = false;
      return;
    }
    const api = ApiFactory.authenticated();
    const r = await api.listStockLocations(this.activeShop.shopName);
    if (r.success && r.data) this._items = r.data;
    else if (!r.success)
      this._error = r.error ?? 'Failed to load stock locations.';
    this._loading = false;
  }

  private _add() {
    const next = (this._items[this._items.length - 1]?.sortOrder ?? 0) + 10;
    this._items = [
      ...this._items,
      { id: '', name: '', sortOrder: next, isActive: true },
    ];
    this._saved = false;
  }

  private _remove(idx: number) {
    this._items = this._items.filter((_, i) => i !== idx);
    this._saved = false;
  }

  private _update(idx: number, patch: Partial<IStockLocation>) {
    this._items = this._items.map((l, i) =>
      i === idx ? { ...l, ...patch } : l
    );
    this._saved = false;
  }

  // Move a row up or down in the displayed order. Adjusts SortOrder so
  // the server-side ordering matches what the operator sees.
  private _move(idx: number, delta: -1 | 1) {
    const target = idx + delta;
    if (target < 0 || target >= this._items.length) return;
    const next = [...this._items];
    [next[idx], next[target]] = [next[target], next[idx]];
    // Renumber SortOrder so it matches displayed order (10-spaced for
    // future manual insertions).
    this._items = next.map((l, i) => ({ ...l, sortOrder: (i + 1) * 10 }));
    this._saved = false;
  }

  private _seedDefaults() {
    if (this._items.length > 0) return;
    this._items = SEED_LOCATIONS.map((l) => ({
      ...l,
      id: l.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, ''),
    }));
    this._saved = false;
  }

  private async _save() {
    if (this._saving || !this.activeShop) return;
    this._error = null;
    this._saved = false;

    const seen = new Set<string>();
    for (const l of this._items) {
      const id = l.id.trim().toLowerCase();
      if (!SLUG_RX.test(id)) {
        this._error = `"${l.id}" is not a valid id (lowercase letters, digits, hyphens; 2–48 chars).`;
        return;
      }
      if (seen.has(id)) {
        this._error = `Duplicate id "${id}".`;
        return;
      }
      seen.add(id);
      if (!l.name.trim()) {
        this._error = `Location "${id}" needs a name.`;
        return;
      }
    }

    this._saving = true;
    try {
      const api = ApiFactory.authenticated();
      const cleaned: IStockLocation[] = this._items.map((l) => ({
        ...l,
        id: l.id.trim().toLowerCase(),
        name: l.name.trim(),
      }));
      const r = await api.replaceStockLocations(
        this.activeShop.shopName,
        cleaned
      );
      if (!r.success || !r.data) {
        this._error = r.error ?? 'Save failed.';
        return;
      }
      this._items = r.data;
      this._saved = true;
    } finally {
      this._saving = false;
    }
  }

  private _back() {
    router.navigate(resolveRouterPath('stock'));
  }

  render() {
    if (this._loading) return html`<p>Loading…</p>`;
    if (this._error && this._items.length === 0) {
      return html`<sl-alert variant="danger" open>${this._error}</sl-alert>`;
    }

    return html`
      <header class="bar">
        <h2 style="margin: 0;">Stock locations</h2>
        <sl-button size="small" variant="text" @click=${this._back}>
          ← Back to stock
        </sl-button>
      </header>

      <small class="small">
        Order these so they match the way you walk the shop during a count. The
        stock-take screen groups lines by location in this order; untagged stock
        items render under <strong>Unsorted</strong> at the end.
      </small>

      ${
        this._items.length === 0
          ? html`
              <div class="empty">
                <p>No stock locations yet.</p>
                <div
                  style="display: flex; gap: 8px; flex-wrap: wrap; justify-content: center;"
                >
                  <sl-button variant="primary" @click=${this._seedDefaults}>
                    Seed common cafe locations
                  </sl-button>
                  <sl-button @click=${this._add}>Start blank</sl-button>
                </div>
              </div>
            `
          : html`
              <div class="row-list">
                ${this._items.map(
                  (l, i) => html`
                    <div class="row">
                      <div class="reorder">
                        <sl-icon-button
                          name="chevron-up"
                          label="Move up"
                          ?disabled=${i === 0}
                          @click=${() => this._move(i, -1)}
                        ></sl-icon-button>
                        <sl-icon-button
                          name="chevron-down"
                          label="Move down"
                          ?disabled=${i === this._items.length - 1}
                          @click=${() => this._move(i, 1)}
                        ></sl-icon-button>
                      </div>
                      <sl-input
                        size="small"
                        placeholder="id"
                        .value=${l.id}
                        @sl-change=${(e: Event) =>
                          this._update(i, {
                            id: (
                              e.target as HTMLInputElement
                            ).value.toLowerCase(),
                          })}
                      ></sl-input>
                      <sl-input
                        size="small"
                        placeholder="Name (e.g. Walk-in)"
                        .value=${l.name}
                        @sl-change=${(e: Event) =>
                          this._update(i, {
                            name: (e.target as HTMLInputElement).value,
                          })}
                      ></sl-input>
                      <label
                        style="display: flex; gap: 6px; align-items: center;"
                      >
                        <sl-switch
                          ?checked=${l.isActive}
                          @sl-change=${(e: Event) =>
                            this._update(i, {
                              isActive: (e.target as HTMLInputElement).checked,
                            })}
                        ></sl-switch>
                        <small>Active</small>
                      </label>
                      <sl-icon-button
                        name="trash"
                        label="Remove"
                        @click=${() => this._remove(i)}
                      ></sl-icon-button>
                    </div>
                  `
                )}
              </div>
            `
      }
      ${
        this._error
          ? html`<sl-alert variant="danger" open style="margin-top: 8px;"
              >${this._error}</sl-alert
            >`
          : nothing
      }
      ${
        this._saved
          ? html`<sl-alert
              variant="success"
              open
              closable
              style="margin-top: 8px;"
              >Saved.</sl-alert
            >`
          : nothing
      }

      <div class="actions">
        <sl-button @click=${this._add}>
          <sl-icon slot="prefix" name="plus-lg"></sl-icon>
          Add location
        </sl-button>
        <sl-button
          variant="primary"
          @click=${this._save}
          ?loading=${this._saving}
          ?disabled=${this._saving}
        >
          Save
        </sl-button>
      </div>
    `;
  }
}
